import React, { useMemo, useState, useEffect, useRef } from "react";
import axios from "axios";

/**
 * Formulario con búsqueda predictiva de SN, validación online de CUE
 * y bloqueo de correos oficiales institucionales.
 */
export default function ConsultaForm({
  onGuardar,
  baseUrl = "https://pjb.edu.ar/api",
  token = "a3f19c8e2b7d4f6a1c0e5b9d7a2f3c8e6d1b4a7f9e2c5d0b8a6c3e1f7d4b2aa9",
  saving = false,
}) {
  const [email, setEmail] = useState("");
  const [nombre, setNombre] = useState("");
  const [apellido, setApellido] = useState("");
  const [cue, setCue] = useState("");
  const [serial, setSerial] = useState("");
  const [infoSN, setInfoSN] = useState(null);
  const [errorSN, setErrorSN] = useState(null);
  const [snMatches, setSnMatches] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [infoCUE, setInfoCUE] = useState(null);
  const [errorCUE, setErrorCUE] = useState(null);

  const cueSan = cue.replace(/\D+/g, "");
  const serialSan = serial.replace(/\s+/g, "").toUpperCase();
  const snInputRef = useRef(null);

  // --- Validaciones de Email ---
  const emailOk = useMemo(() => /.+@.+\..+/.test(email.trim()), [email]);
  // Bloqueo de correos que empiezan con EE (oficiales de escuela)
  const isOfficialEmail = useMemo(() => /^EE\d+/i.test(email.trim()), [email]);

  const cueOk = cueSan.length === 9;
  const serialOk = serialSan.length >= 10 && serialSan.length <= 24;

  // El botón se bloquea si falta algo, si está guardando o si el mail es oficial
  const disabled =
    !(emailOk && cueOk && serialOk && infoSN && infoCUE) || isOfficialEmail || saving;

  useEffect(() => {
    if (snInputRef.current) {
      snInputRef.current.focus();
    }
  }, []);

  // 🟡 Buscar sugerencias SN
  useEffect(() => {
    if (serialSan.length < 6) {
      setSnMatches([]);
      setShowSuggestions(false);
      return;
    }

    const fetchMatches = async () => {
      try {
        const res = await axios.get(
          `${baseUrl}/desbloqueos_v2/buscar_sn.php?sn=${serialSan}&exact=0&limit=10`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Origin: "https://api.pjb.edu.ar",
            },
          }
        );
        if (res.data.ok && res.data.count > 0) {
          setSnMatches(res.data.data);
          setShowSuggestions(true);
        } else {
          setSnMatches([]);
          setShowSuggestions(false);
        }
      } catch (err) {
        console.error("Error buscando coincidencias SN:", err);
      }
    };

    const delay = setTimeout(fetchMatches, 500);
    return () => clearTimeout(delay);
  }, [serialSan, baseUrl, token]);

  const handleSelectSN = (sn) => {
    setSerial(sn);
    setShowSuggestions(false);
    setSnMatches([]);
  };

  // 🧩 Validar S/N exacto
  useEffect(() => {
    if (serialSan.length < 10) return;
    setErrorSN(null);
    setInfoSN(null);

    const fetchSN = async () => {
      try {
        const res = await axios.get(
          `${baseUrl}/desbloqueos_v2/buscar_sn.php?sn=${serialSan}&exact=1`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Origin: "https://api.pjb.edu.ar",
            },
          }
        );
        if (res.data.ok && res.data.count > 0) {
          setInfoSN(res.data.data[0]);
        } else {
          setErrorSN("No se encontró el número de serie.");
        }
      } catch (err) {
        setErrorSN("Error al consultar la API de SN.");
      }
    };

    const delay = setTimeout(fetchSN, 700);
    return () => clearTimeout(delay);
  }, [serialSan, baseUrl, token]);

  // 🏫 Validar CUE
  useEffect(() => {
    if (!cueOk) return;
    setErrorCUE(null);
    setInfoCUE(null);

    const fetchCUE = async () => {
      try {
        const res = await axios.get(
          `https://escuelas.educacioncba.edu.ar/api/escuela/cue/${cueSan}`
        );
        const data = res.data?.data?.[0];
        if (data) {
          setInfoCUE(data);
        } else {
          setErrorCUE("No se encontró el CUE ingresado.");
        }
      } catch (err) {
        setErrorCUE("CUE no se encuentra en nuestros registros.");
      }
    };

    const delay = setTimeout(fetchCUE, 700);
    return () => clearTimeout(delay);
  }, [cueSan]);

  const handleGuardar = () => {
    if (disabled) return;
    onGuardar?.({
      email: email.trim(),
      nombre: nombre.trim(),
      apellido: apellido.trim(),
      cue: cueSan,
      serialNumber: serialSan,
      escuela: infoCUE?.cue?.nombre,
      localidad: infoCUE?.ubicacion?.localidad?.nombre,
      snInfo: infoSN,
    });
  };

  return (
    <div className="card mt-4 shadow-sm">
      <div className="card-body row g-3">
        {/* S/N */}
        <div className="col-12 position-relative">
          <label className="form-label">S/N (Serial)</label>
          <input
            type="text"
            ref={snInputRef}
            className={`form-control ${
              serialSan.length ? (serialOk ? "is-valid" : "is-invalid") : ""
            }`}
            value={serial}
            onChange={(e) => setSerial(e.target.value)}
            placeholder="Ej: 81CD781AF38F61C49A42"
            autoComplete="off"
            onFocus={() => {
              if (snMatches.length > 0) setShowSuggestions(true);
            }}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          />

          {showSuggestions && snMatches.length > 0 && (
            <ul
              className="list-group position-absolute w-100 mt-1 shadow-sm"
              style={{ zIndex: 10, maxHeight: "220px", overflowY: "auto" }}
            >
              {snMatches.map((row, i) => (
                <li
                  key={i}
                  className="list-group-item list-group-item-action"
                  onClick={() => handleSelectSN(row.sn)}
                  style={{ cursor: "pointer" }}
                >
                  <b>{row.sn}</b>
                  {row.nombre && <span className="text-muted ms-2">({row.nombre})</span>}
                </li>
              ))}
            </ul>
          )}

          {infoSN && (
            <div className="alert alert-success mt-2 p-2">
              ✅ <b>Equipo:</b> {infoSN.nombre || "sin nombre registrado"}
            </div>
          )}
          {errorSN && <div className="alert alert-warning mt-2 p-2">{errorSN}</div>}
        </div>

        {/* CUE */}
        <div className="col-12 col-md-6">
          <label className="form-label">CUE</label>
          <input
            type="text"
            className={`form-control ${
              cue.length === 0 ? "" : cueOk ? "is-valid" : "is-invalid"
            }`}
            value={cue}
            onChange={(e) => setCue(e.target.value)}
            placeholder="Ej: 140568400"
          />
          {infoCUE && (
            <div className="alert alert-success mt-2 p-2">
              🏫 <b>{infoCUE.cue?.nombre}</b>
              <br />
              <small>
                {infoCUE?.ubicacion?.localidad?.nombre},{" "}
                {infoCUE?.ubicacion?.departamento?.nombre}
              </small>
            </div>
          )}
          {errorCUE && <div className="alert alert-warning mt-2 p-2">{errorCUE}</div>}
        </div>

        {/* Email con bloqueo de oficiales EE... */}
        <div className="col-12 col-md-6">
          <label className="form-label">Correo electrónico</label>
          <input
            type="email"
            className={`form-control ${
              email.length ? (emailOk && !isOfficialEmail ? "is-valid" : "is-invalid") : ""
            }`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="ejemplo@correo.com"
          />
          {isOfficialEmail && (
            <div className="alert alert-danger mt-2 p-2 small">
              <b>Por seguridad y para garantizar la recepción del código, no se receptan pedidos de correos oficiales (EE*******)</b>. Por favor, utilice otra dirección.
            </div>
          )}
        </div>

        {/* Nombre y Apellido */}
        <div className="col-12 col-md-6">
          <label className="form-label">Nombre</label>
          <input
            type="text"
            className="form-control"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
          />
        </div>
        <div className="col-12 col-md-6">
          <label className="form-label">Apellido</label>
          <input
            type="text"
            className="form-control"
            value={apellido}
            onChange={(e) => setApellido(e.target.value)}
          />
        </div>

        {/* Botón Guardar */}
        <div className="col-12 d-flex justify-content-end mt-3">
          <button
            className="btn btn-outline-secondary"
            onClick={handleGuardar}
            disabled={disabled}
          >
            {saving ? "Guardando…" : isOfficialEmail ? "Email no permitido" : "Guardar consulta"}
          </button>
        </div>
      </div>
    </div>
  );
}